import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { api, signOut } from '../api';
import { useMeResponse } from '../App';
import { Passkeys } from '../components/Passkeys';
import { ReleaseBadge } from '../components/ReleaseBadge';

/** Signed in via Authentik, but Fyr hasn't granted any access yet (Authentik authenticates; Fyr authorises). */
export function PendingPage() {
  const { t } = useTranslation();
  const me = useMeResponse();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (me.isLoading) return <div className="center-msg">…</div>;
  if (me.isError || !me.data) return <Navigate to="/login" replace />;
  if (!me.data.pending) return <Navigate to="/" replace />;
  const p = me.data.person;
  return (
    <div className="login">
      <div className="login-card">
        <div className="login-head">
          <img src="/favicon.svg" alt="" width={36} height={36} />
          <div>
            <h1>
              {t('pending.title', { name: p.givenName ?? p.displayName })} <ReleaseBadge />
            </h1>
            <p className="muted">{t('pending.subtitle')}</p>
          </div>
        </div>
        <p>{t('pending.body')}</p>
        {/* #142 M19 a private workspace of one's own while waiting for an organisation */}
        <div className="pending-personal">
          <p className="muted small">{t('pending.personalHint')}</p>
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await api.post('/auth/personal');
                qc.clear();
                nav('/', { replace: true });
              } catch (e) {
                setErr((e as Error).message);
                setBusy(false);
              }
            }}
          >
            {t('pending.personal')}
          </button>
          {err && <p className="alert">{err}</p>}
        </div>
        <Passkeys compact />
        <dl className="kv">
          <dt>{t('profile.name')}</dt>
          <dd>{p.displayName}</dd>
          <dt>{t('profile.email')}</dt>
          <dd>{p.email}</dd>
          {p.defaultAffiliation && (
            <>
              <dt>{t('profile.suggestedAffiliation')}</dt>
              <dd>
                <span className={`badge aff-${p.defaultAffiliation}`}>
                  {t(`affiliation.${p.defaultAffiliation}`)}
                </span>
              </dd>
            </>
          )}
        </dl>
        <button className="btn" onClick={() => void signOut()}>
          {t('nav.signOut')}
        </button>
      </div>
    </div>
  );
}
